import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, MailX, CheckCircle2 } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { Switch } from "../components/ui/switch";
import { FaqList } from "../components/site/FaqList";

const Shell = ({ children, testid }) => (
  <div className="relative py-20 px-5 min-h-[70vh]" data-testid={testid}><div className="absolute inset-0 grid-bg opacity-40" />
    <div className="relative max-w-md mx-auto card-surface p-8">{children}</div></div>
);

export function Unsubscribe() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [info, setInfo] = useState(null);
  const [state, setState] = useState("loading");
  useEffect(() => {
    if (!token) return setState("invalid");
    api.get("/unsubscribe/info", { params: { token } }).then((r) => { setInfo(r.data); setState("ready"); }).catch(() => setState("invalid"));
  }, [token]);
  const confirm = async () => {
    try { await api.post("/unsubscribe", { token }); setState("done"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Shell testid="unsubscribe-page">
      {state === "loading" && <Loader2 className="animate-spin text-[#B7FF3C] mx-auto" />}
      {state === "invalid" && <><h1 className="text-2xl font-black uppercase">Link invalid</h1><p className="mt-2 text-sm text-[#A3ADA3]">This unsubscribe link is invalid. You can manage preferences from <Link to="/dashboard" className="text-[#B7FF3C]">View My Stats</Link>.</p></>}
      {state === "ready" && <>
        <MailX className="text-[#B7FF3C]" />
        <h1 className="mt-3 text-3xl font-black uppercase">Unsubscribe</h1>
        <p className="mt-2 text-sm text-[#A3ADA3]">Stop promotional emails to <span className="text-white">{info.email_masked}</span>? You'll still receive essential account emails such as verification codes.</p>
        <button onClick={confirm} data-testid="unsubscribe-confirm-btn" className="mt-6 w-full py-3.5 rounded-full bg-[#B7FF3C] text-[#080A08] font-bold">Unsubscribe from promotional emails</button>
      </>}
      {state === "done" && <div data-testid="unsubscribe-done"><CheckCircle2 className="text-[#B7FF3C]" size={40} /><h1 className="mt-3 text-2xl font-black uppercase">You're unsubscribed</h1><p className="mt-2 text-sm text-[#A3ADA3]">You won't receive promotional emails from CricAnalyze. You can opt back in any time from <Link to={`/email-preferences?token=${token}`} className="text-[#B7FF3C]">email preferences</Link>.</p></div>}
    </Shell>
  );
}

export function EmailPreferences() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [p, setP] = useState(null);
  const [state, setState] = useState("loading");
  useEffect(() => {
    api.get("/email-preferences", { params: token ? { token } : {} }).then((r) => { setP(r.data); setState("ready"); }).catch(() => setState("auth"));
  }, [token]);
  const update = async (v) => {
    try { const r = await api.post("/email-preferences", { marketing_consent: v, token }); setP({ ...p, marketing_consent: r.data.marketing_consent }); toast.success("Preferences saved"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Shell testid="email-preferences-page">
      <h1 className="text-3xl font-black uppercase">Email Preferences</h1>
      {state === "loading" && <Loader2 className="animate-spin text-[#B7FF3C] mt-6" />}
      {state === "auth" && <p className="mt-3 text-sm text-[#A3ADA3]">To manage preferences, open the link from any CricAnalyze email or verify via <Link to="/dashboard" className="text-[#B7FF3C]">View My Stats</Link>.</p>}
      {state === "ready" && <>
        <p className="mt-2 text-sm text-[#A3ADA3]">For {p.email_masked}</p>
        <div className="mt-6 flex items-center justify-between gap-4 rounded-xl border border-[#2A362A] p-4">
          <div><p className="font-semibold text-sm">Promotional emails</p><p className="text-xs text-[#7C8A7C]">Product news, launch announcements, offers.</p></div>
          <Switch checked={p.marketing_consent} onCheckedChange={update} data-testid="prefs-marketing-switch" className="data-[state=checked]:bg-[#B7FF3C]" />
        </div>
        <div className="mt-3 flex items-center justify-between gap-4 rounded-xl border border-[#2A362A] p-4 opacity-70">
          <div><p className="font-semibold text-sm">Essential account emails</p><p className="text-xs text-[#7C8A7C]">Verification codes, reward status. Always on.</p></div>
          <Switch checked disabled />
        </div>
      </>}
    </Shell>
  );
}

export function FaqPage() {
  return (
    <div className="max-w-3xl mx-auto px-5 py-20" data-testid="faq-page">
      <p className="eyebrow">Help centre</p>
      <h1 className="mt-3 text-5xl font-black uppercase">Frequently Asked Questions</h1>
      <FaqList />
    </div>
  );
}
